import { UpperCaseFirstLetterPipe } from '../../../../../util/pipes/uppercase-first-letter.pipe';
import { FinancialStatementItem } from '../../../../financial-statement/models/common/FinancialStatementItem';
import * as XLSX from 'xlsx-js-style';
import { InvestorCapitalAccount } from '../../../../financial-statement/models/capital-account/InvestorCapitalAccount';
import {
  InvestorCapitalAccountDTO,
} from '../../../../financial-statement/models/DTOs/capital-account/InvestorCapitalAccountDTO';
import { formats, getCell, setColumnWidth, setRowHeight, withCellStyles } from '../../../utils';
import {
  bold,
  boldCentered,
  boldCenteredLightOrangeBg,
  boldGrayBg,
  boldLightGrayBg,
  boldRed,
  center,
  lightOrangeBg,
} from './styles';
import { fitToColumn } from '../utils';
import { WorkSheet } from 'xlsx-js-style';
import {
  AllocationMethod,
  CapitalAccountData, CarriedInterestAllocationMethod,
  InvestorDataCA,
} from 'src/app/domain/financial-statement/utils/capital-account/utils';
import { ItemManualValueData, parseItemManualValuesFromExcel } from './item-manual-values-parser';
import { intToExcelCol } from 'excel-column-name';
import { Rows, Row } from '../../../interfaces';

export class CapitalAccountMapper {
  private indexes: Indexes;

  /**
   * This method is used when there are **existing** investors in the system, and the user wants to
   * download investors only as an Excel file by clicking on 'Download template'.
   */
  mapInvestorsToTemplateData(investors: InvestorCapitalAccount[]): Rows {
    return investors.map((investor) => [
      investor.code,
      investor.name,
      investor.isGP ? 'GP' : '',
      investor.commitment,
    ]);
  }

  /**
   * This method is used when there are **no** existing investors in the system, and the user wants to
   * download the template as an Excel file by clicking on 'Download template'.
   */
  createTemplateData(orgID: number, fundID: string): Rows {
    const code = orgID.toString() + '-' + fundID;
    const rows: Rows = [];
    rows.push([code + '-1', 'Nathaniel Fitzwilliam', false, 75000]);
    rows.push([code + '-2', 'Isabella Montgomery-Winthrop', true, 175000]);
    return rows;
  }

  /**
   * Converts data from an uploaded Excel file to an array of InvestorCapitalAccountDTO.
   */
  mapExcelDataToInvestors(data: unknown[]): InvestorCapitalAccountDTO[] {
    const trimmedHeaders = this.mapTrimmedHeaders(Object.keys(data[0]));
    return data.map((investorExcel) => this.mapExcelInvestorToInvestorDTO(investorExcel, trimmedHeaders));
  }

  mapExcelDataToManualValues(data: any[]): ItemManualValueData[] {
    return parseItemManualValuesFromExcel(data);
  }

  /**
   * Prepares data from the capital account for export as an Excel file.
   * This method organizes elements of the capital account data (like headers, investor data, and totals)
   * into a structured format suitable for Excel export.
   */
  prepareData(capitalAccountData: CapitalAccountData): XLSX.WorkSheet {
    const rows: Rows = [];
    this.setIndexes(capitalAccountData);

    this.addHeaderRows(rows, capitalAccountData);
    this.addInvestorDataRows(rows, capitalAccountData);
    this.addTotalsRows(rows, capitalAccountData);

    const sheet: XLSX.WorkSheet = {
      ...XLSX.utils.aoa_to_sheet(rows),
      '!cols': fitToColumn(rows),
    };
    this.setManualRowHeights(sheet);
    this.setManualColumnWidths(sheet);

    return sheet;
  }

  private mapExcelInvestorToInvestorDTO(investorExcel: unknown, trimmedHeaders: Record<string, string>): InvestorCapitalAccountDTO {
    return {
      uuid: null,
      code: this.removeLeadingZerosFromId(String(investorExcel[trimmedHeaders.ID]) || ''),
      name: String(investorExcel[trimmedHeaders.Name]).trim() || '',
      isGP: this.parseGP(investorExcel[trimmedHeaders.GP]),
      commitment: this.parseCommitment(investorExcel[trimmedHeaders.Commitment] || 0),
    };
  }

  /**
   * Adds header rows to the Excel data array.
   * This method creates two types of header rows: the first contains general information such as investor details and
   * financial statement item names, while the second row includes the allocation methods for each FS item.
   */
  private addHeaderRows(rows: Rows, capitalAccountData: CapitalAccountData): void {
    const headerRow: Row = this.combineHeaders(capitalAccountData);
    const allocationMethodRow: Row = this.createAllocationMethodRow(capitalAccountData);

    rows.push(headerRow);
    rows.push(allocationMethodRow);
  }

  /**
   * Adds rows for Limited Partners (LPs) and General Partners (GPs) to the Excel data array.
   * This method first maps data for LPs, including their details and subtotals, followed by a similar process for GPs.
   */
  private addInvestorDataRows(rows: Rows, capitalAccountData: CapitalAccountData): void {
    const lpsRows = this.mapInvestorsDataToExcel(capitalAccountData, 'lps');
    const gpsRows = this.mapInvestorsDataToExcel(capitalAccountData, 'gps');

    rows.push([withCellStyles('Limited partners (LP)', bold)]);
    rows.push(...lpsRows);

    rows.push([withCellStyles('General partners (GP)', bold)]);
    rows.push(...gpsRows);
  }

  private addTotalsRows(rows: Rows, capitalAccountData: CapitalAccountData): void {
    rows.push(this.addGrandTotalRow());
    rows.push(this.addAssemblyTotalRow(capitalAccountData));
    rows.push(this.addCheckRow());
  }

  private setIndexes(capitalAccountData: CapitalAccountData): void {
    this.indexes = new Indexes();
    this.indexes.lpsStart = 4; // after headers, alloc methods and 'Limited partners (LP)' row
    this.indexes.gpsStart = capitalAccountData.investorsData.filter((inv) => !inv.investor.isGP).length
      + this.indexes.lpsStart + 2; // after lp rows, including subtotal row and 'General partners (GP)' row
    this.indexes.commitmentColumn = 3;
    this.indexes.holdingsColumns = [4, 5];
    this.indexes.firstItemColumn = 7; // after ID, Name, Commitment, Holding, Without GP, and blank space
    this.setItemsIndexes(capitalAccountData);
    this.setBlankSpaceIndexes(capitalAccountData);
    this.setCustomAllocationsIndexes(capitalAccountData);
    this.setAccumulatedResultsIndex();
    this.indexes.assemblyTotalRow = this.indexes.lpsStart + capitalAccountData.investorsData.length + 5;
    this.indexes.carriedInterestColumns = [this.indexes.accumulatedResultsColumn + 2, this.indexes.accumulatedResultsColumn + 3];
    this.indexes.navColumn = this.indexes.carriedInterestColumns.at(-1) + 2; // 1 to be the next column
    this.indexes.nglColumn = this.indexes.navColumn + 2;
    this.indexes.tvpiColumn = this.indexes.nglColumn + 1;
  }

  private setItemsIndexes(capitalAccountData: CapitalAccountData): void {
    let currentItemIndex = this.indexes.firstItemColumn;
    capitalAccountData.partnersCapital.financialStatementItems.forEach((item) => {
      this.indexes.itemsColumns.set(item.name, currentItemIndex++);
    });
    currentItemIndex++;
    capitalAccountData.profits.financialStatementItems.forEach((item) => {
      this.indexes.itemsColumns.set(item.name, currentItemIndex++);
    });
    currentItemIndex++;
    capitalAccountData.losses.financialStatementItems.forEach((item) => {
      this.indexes.itemsColumns.set(item.name, currentItemIndex++);
    });
  }

  private setBlankSpaceIndexes(capitalAccountData: CapitalAccountData): void {
    const afterInvestorInfo = this.indexes.holdingsColumns.at(-1) + 1;
    const afterPartnersCapital = afterInvestorInfo + capitalAccountData.partnersCapital.financialStatementItems.length + 1;
    const afterProfits = afterPartnersCapital + capitalAccountData.profits.financialStatementItems.length + 1;
    const afterLosses = afterProfits + capitalAccountData.losses.financialStatementItems.length + 1;
    const afterCustomAllocations = afterLosses + capitalAccountData.customAllocations.length + 1;
    const afterAccumulatedResults = afterCustomAllocations + 2;
    const afterCarriedInterest = afterAccumulatedResults + 3; // carried interest is 2 columns
    const afterNAV = afterCarriedInterest + 2;
    this.indexes.blankSpacesColumns = [afterInvestorInfo, afterPartnersCapital, afterProfits, afterLosses,
      afterCustomAllocations, afterAccumulatedResults, afterCarriedInterest, afterNAV];
  }

  /**
   * Custom allocations columns are after fs items, we need these indexes to be skipped in check
   */
  private setCustomAllocationsIndexes(capitalAccountData: CapitalAccountData): void {
    for (let i = 0; i <= capitalAccountData.customAllocations.length; i++) {
      this.indexes.customAllocationsColumns.push(this.indexes.blankSpacesColumns.at(3) + i);
    }
  }

  private setAccumulatedResultsIndex(): void {
    if (!this.indexes.customAllocationsColumns.length) {
      this.indexes.accumulatedResultsColumn = this.indexes.blankSpacesColumns.at(3) + 2;
      return;
    }
    this.indexes.accumulatedResultsColumn = this.indexes.customAllocationsColumns.at(-1) + 2;
  }

  private combineHeaders(capitalAccountData: CapitalAccountData): string[] {
    const upperCaseFirstLetterPipe = new UpperCaseFirstLetterPipe();
    const investorsHeaders = ['ID', 'Name', 'Commitment', 'Holding, %', 'Without GP, %'];

    const partnersCapitalHeaders = capitalAccountData.partnersCapital.financialStatementItems.map((item) =>
      upperCaseFirstLetterPipe.transform(item.name),
    );
    const profitsHeaders = capitalAccountData.profits.financialStatementItems.map((item) =>
      upperCaseFirstLetterPipe.transform(item.name),
    );
    const lossesHeaders = capitalAccountData.losses.financialStatementItems.map((item) =>
      upperCaseFirstLetterPipe.transform(item.name),
    );
    const customAllocationsHeaders = capitalAccountData.customAllocations.map((allocation) =>
      upperCaseFirstLetterPipe.transform(allocation.name),
    );

    const accumulatedResults = 'Accumulated results';
    let carriedInterestPct = 'Carried \n interest, %';
    if (capitalAccountData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.CALCULATED) {
      carriedInterestPct = (capitalAccountData.carriedInterest.manualPct * 100).toString() + '%';
    }
    const carriedInterest = 'Carried \n interest';
    const netGainLoss = 'Net Gain/(Loss)';
    const tvpi = 'TVPI';
    const nav = 'Partners\' capital (NAV)';

    return [...investorsHeaders, blankSpace, ...partnersCapitalHeaders, blankSpace, ...profitsHeaders,
      blankSpace, ...lossesHeaders, blankSpace, ...customAllocationsHeaders, blankSpace,
      accumulatedResults, blankSpace, carriedInterestPct, carriedInterest, blankSpace, nav, blankSpace, netGainLoss, tvpi];
  }

  private createAllocationMethodRow(capitalAccountData: CapitalAccountData): Row {
    const row: Row = [];

    const partnersCapitalAllocation = capitalAccountData.partnersCapital.financialStatementItems.map(
      (item) => this.getAllocationMethodBoldCentered(item.id, capitalAccountData),
    );
    const profitsAllocation = capitalAccountData.profits.financialStatementItems.map(
      (item) => this.getAllocationMethodBoldCentered(item.id, capitalAccountData),
    );
    const lossesAllocation = capitalAccountData.losses.financialStatementItems.map(
      (item) => this.getAllocationMethodBoldCentered(item.id, capitalAccountData),
    );
    const customAllocations = capitalAccountData.customAllocations.map(
      () => withCellStyles(AllocationMethod.MANUAL, boldCenteredLightOrangeBg),
    );

    const carriedInterestMethod = capitalAccountData.carriedInterest.selector.selectedMethod;
    const carriedInterestStyle = carriedInterestMethod === CarriedInterestAllocationMethod.CALCULATED ? boldCentered : boldCenteredLightOrangeBg;

    // fill the columns corresponding to investor details like id, name, commitment, holdings
    row.push(...Array(this.indexes.firstItemColumn - 1).fill(blankSpace));
    row.push(...partnersCapitalAllocation, blankSpace, ...profitsAllocation, blankSpace, ...lossesAllocation);
    row.push(blankSpace, ...customAllocations);
    row.push(blankSpace, blankSpace, blankSpace, blankSpace); // skip accumulated results and carried interest pct
    row.push(withCellStyles(carriedInterestMethod, carriedInterestStyle));

    return row;
  }

  private getAllocationMethodBoldCentered(itemId: number, capitalAccountData: CapitalAccountData): any {
    const allocationMethod = capitalAccountData.itemAllocationMethods.find(
      (itemAllocMethod) => itemAllocMethod.item.id === itemId).method;
    if (allocationMethod === AllocationMethod.MANUAL) {
      return withCellStyles(allocationMethod, boldCenteredLightOrangeBg);
    }
    return withCellStyles(allocationMethod, boldCentered);
  }

  private mapInvestorsDataToExcel(capitalAccountData: CapitalAccountData, type: 'lps' | 'gps'): Rows {
    const isGP: boolean = type === 'gps';
    const desiredInvestors = capitalAccountData.investorsData.filter((inv) => inv.investor.isGP === isGP);
    const startRowIndex = isGP ? this.indexes.gpsStart : this.indexes.lpsStart;
    const rows: Rows = [];

    desiredInvestors.map((investorData, invIdx) => {
      const row: Row = [];
      const excelRowIndex = startRowIndex + invIdx;
      this.addInvestorValuesToRow(row, investorData);
      this.addPartValuesToRow(row, investorData, capitalAccountData, excelRowIndex);
      this.addCustomAllocationValuesToRow(row, investorData, capitalAccountData);
      this.addAccumulatedResultsToRow(row, capitalAccountData, excelRowIndex);
      this.addCarriedInterestToRow(row, investorData, capitalAccountData);
      this.addNavToRow(row, excelRowIndex);
      this.addNglToRow(row, excelRowIndex);
      this.addTvpiToRow(row, excelRowIndex);

      rows.push(row);
    });

    if (isGP) {
      this.addCarriedInterestRow(rows, capitalAccountData);
    }

    const subtotalRow = this.addSubtotalRow(startRowIndex, desiredInvestors.length, isGP);
    rows.push(subtotalRow);

    return rows;
  }

  private addInvestorValuesToRow(row: Row, investorData: InvestorDataCA) {
    row.push(
      [investorData.investor.code],
      [investorData.investor.name],
      getCell({ v: investorData.investor.commitment, z: formats.thousandsSeparator, t: 'n' }),
      getCell({ v: investorData.holding, z: formats.toFixed2, t: 'n', s: center }),
      getCell({ v: investorData.holdingWithoutGP, z: formats.toFixed2, t: 'n', s: center }),
      [blankSpace],
    );
  }

  private addPartValuesToRow(row: Row, investorData: InvestorDataCA, capitalAccountData: CapitalAccountData, invIdx: number): void {
    this.addItemsValues(row, investorData, capitalAccountData.partnersCapital.financialStatementItems, invIdx);
    this.addItemsValues(row, investorData, capitalAccountData.profits.financialStatementItems, invIdx);
    this.addItemsValues(row, investorData, capitalAccountData.losses.financialStatementItems, invIdx);
  }

  private addItemsValues(row: Row, investorData: InvestorDataCA, items: FinancialStatementItem[], invIdx: number): void {
    items.forEach((item) => {
      const allocationMethod = investorData.allocationMethod[item.id];
      if (allocationMethod === AllocationMethod.MANUAL) {
        const value = investorData.manualValue[item.id];
        row.push(getCell({ v: value ?? '', z: formats.thousandsSeparator, t: 'n', s: lightOrangeBg }));
      } else {
        const itemLetter = intToExcelCol(this.indexes.itemsColumns.get(item.name));
        const holding = intToExcelCol(this.indexes.holdingsColumns.at(0));
        const holdingWithoutGP = intToExcelCol(this.indexes.holdingsColumns.at(1));
        const formula = `IF(${itemLetter}2="Holding, %",${holding}${invIdx},${holdingWithoutGP}${invIdx})/100*${itemLetter}${this.indexes.assemblyTotalRow}`;
        row.push(getCell({ f: formula, z: formats.thousandsSeparator, t: 'n' }));
      }
    });
    row.push(blankSpace);
  }

  private addCustomAllocationValuesToRow(row: Row, investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): void {
    capitalAccountData.customAllocations.forEach((customAllocation) => {
      const value = investorData.customAllocationValue[customAllocation.uuid];
      row.push(getCell({ v: value ?? '', z: formats.thousandsSeparator, t: 'n', s: lightOrangeBg }));
    });
    row.push(blankSpace);
  }

  private addAccumulatedResultsToRow(row: Row, capitalAccountData: CapitalAccountData, invIdx: number): void {
    const firstFirstPlItem = this.findFirstPlItemName(capitalAccountData);
    const firstItemLetter = intToExcelCol(this.indexes.itemsColumns.get(firstFirstPlItem));
    const lastColumnLetter = intToExcelCol(this.indexes.accumulatedResultsColumn - 2);
    const formula = `SUM(${firstItemLetter}${invIdx}:${lastColumnLetter}${invIdx})`;

    row.push(getCell({ f: formula, z: formats.thousandsSeparator, t: 'n' }));
    row.push(blankSpace);
  }

  private addCarriedInterestToRow(row: Row, investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): void {
    const pct = this.getCarriedInterestPct(investorData, capitalAccountData);
    const value = this.getCarriedInterestValue(investorData, capitalAccountData);
    const isManual = capitalAccountData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.MANUAL;
    row.push(getCell({ v: pct ?? '', z: formats.percents, t: 'n', s: center }));
    row.push(getCell({ v: value ?? '', z: formats.thousandsSeparator, t: 'n', s: isManual ? lightOrangeBg : '' }));
    row.push(blankSpace);
  }

  private getCarriedInterestPct(investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): number {
    if (capitalAccountData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.CALCULATED) {
      if (investorData.investor.isGP) {
        return investorData.carriedInterest.manualPct;
      }
      // return individual manual pct if present, otherwise return common manual pct
      return investorData.carriedInterest.manualPct || capitalAccountData.carriedInterest.manualPct;
    }
    // if selectedMethod === CarriedInterestAllocationMethod.MANUAL, calculate the pct
    if (!investorData.accumulatedResults) {
      return 0;
    }
    return (-1) * investorData.carriedInterest.manualValue / investorData.accumulatedResults;
  }

  private getCarriedInterestValue(investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): number {
    if (capitalAccountData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.MANUAL) {
      return investorData.carriedInterest.manualValue;
    }
    return investorData.carriedInterest.calculatedValue;
  }

  private addCarriedInterestRow(rows: Rows, capitalAccountData: CapitalAccountData) {
    const hasGPs = capitalAccountData.investorsData.some((inv) => inv.investor.isGP === true);
    const row: Row = [withCellStyles('Carried interest', bold)];
    const emptySpace = Array(this.indexes.carriedInterestColumns.at(0) - 1).fill(blankSpace);
    const carriedInterestLetter = intToExcelCol(this.indexes.carriedInterestColumns.at(1));
    // second subtotal row index has not been reached yet, need to calc
    const lastInvestorRow = this.indexes.lpsStart + capitalAccountData.investorsData.length + 1;
    const firstGpRow = this.indexes.subTotalRows.at(0) + 2;
    let formula = `-SUM(${carriedInterestLetter}${this.indexes.lpsStart}:${carriedInterestLetter}${this.indexes.subTotalRows.at(0) - 1}`;
    if (hasGPs) {
      formula += `,${carriedInterestLetter}${firstGpRow}:${carriedInterestLetter}${lastInvestorRow}`;
    }
    formula += ')';
    row.push(...emptySpace, getCell({ f: formula, t: 'n' , z: formats.thousandsSeparator, s: bold }));
    rows.push(row);
  }

  private addNavToRow(row: Row, rowIndex: number): void {
    const navFormula = this.createNavFormula(rowIndex);
    row.push(navFormula);
    row.push(blankSpace);
  }

  private addNglToRow(row: Row, rowIndex: number): void {
    const accResultsLetter = intToExcelCol(this.indexes.accumulatedResultsColumn);
    const carriedInterestLetter = intToExcelCol(this.indexes.carriedInterestColumns.at(1));
    const formula = `${accResultsLetter}${rowIndex}+${carriedInterestLetter}${rowIndex}`;
    row.push(getCell({ f: formula, z: formats.thousandsSeparator, t: 'n' }));
  }

  private addTvpiToRow(row: Row, rowIndex: number): void {
    // (NAV + |Distribution| - |Contribution received in advance|) / (Contribution - |Due to capital contribution|)
    const nav = intToExcelCol(this.indexes.navColumn);
    const distribution = intToExcelCol(this.indexes.itemsColumns.get('distribution'));
    const contribution = intToExcelCol(this.indexes.itemsColumns.get('contribution'));
    const contributionReceived = intToExcelCol(this.indexes.itemsColumns.get('contribution received in advance'));
    const dueToCapital = intToExcelCol(this.indexes.itemsColumns.get('due to capital contribution'));
    const formulaParts = [];

    if (nav) {
      formulaParts.push(`${nav}${rowIndex}`);
    }

    if (distribution) {
      formulaParts.push(`-ABS(${distribution}${rowIndex})`);
    }

    if (contributionReceived) {
      formulaParts.push(`-ABS(${contributionReceived}${rowIndex})`);
    }

    let denominator = contribution ? `${contribution}${rowIndex}` : '0';
    if (dueToCapital) {
      denominator += ` - ABS(${dueToCapital}${rowIndex})`;
    }

    const formula = `MAX(0, (${formulaParts.join('')})/(${denominator !== '0' ? denominator : '1'}))`;
    row.push(getCell({ f: formula, z: formats.multiplier, t: 'n' }));
  }

  private createNavFormula(excelRowIndex: number): XLSX.CellObject {
    const firstItemLetter = XLSX.utils.encode_col(this.indexes.firstItemColumn - 1);
    // going from accumulated results as custom allocations might be empty
    const lastCustomAllocationLetter = XLSX.utils.encode_col(this.indexes.accumulatedResultsColumn - 3);
    const carriedInterestLetter = XLSX.utils.encode_col(this.indexes.carriedInterestColumns.at(1) - 1);

    let formula = `SUM(${firstItemLetter}${excelRowIndex}:${lastCustomAllocationLetter}${excelRowIndex})`;
    formula += `+${carriedInterestLetter}${excelRowIndex}`;

    return { f: formula, t: 'n' , z: formats.thousandsSeparator };
  }

  private addSubtotalRow(startRowIndex: number, numberOfInvestors: number, isGP: boolean): Row {
    const subtotalRow: Row = [ { v: 'Subtotal', s: boldLightGrayBg }, { v: blankSpace, s: boldLightGrayBg } ];
    const endRowIndex = startRowIndex + numberOfInvestors - 1; // -1 because end row is the last investor row
    this.indexes.subTotalRows.push(endRowIndex + (isGP ? 2 : 1));

    // SUM formulas from column C(starting from Commitment) to the NGL column
    for (let colIndex = this.indexes.commitmentColumn; colIndex <= this.indexes.tvpiColumn; colIndex++) {
      if (this.indexes.blankSpacesColumns.includes(colIndex)) {
        subtotalRow.push(blankSpace);
        continue;
      }

      if (colIndex === (this.indexes.carriedInterestColumns.at(0)) || colIndex === this.indexes.tvpiColumn) {
        subtotalRow.push(withCellStyles('', boldLightGrayBg));
        continue;
      }

      const colLetter = intToExcelCol(colIndex);
      let formula = `SUM(${colLetter}${startRowIndex}:${colLetter}${endRowIndex})`;
      if (colIndex === this.indexes.carriedInterestColumns.at(1) && isGP) {
        formula += `+${colLetter}${startRowIndex + 1}`;
      }

      const cellData: XLSX.CellObject = {
        v: 0,
        t: 'n',
        z: this.indexes.holdingsColumns.includes(colIndex) ? formats.toFixed2 : formats.thousandsSeparator,
        s: boldLightGrayBg,
      };

      if (numberOfInvestors) {
        cellData.f = formula;
      }

      subtotalRow.push(cellData);
    }

    return subtotalRow;
  }

  private addGrandTotalRow(): Row {
    const grandTotalRow: Row = [ { v: 'Grand total', s: boldGrayBg }, { v: blankSpace, s: boldGrayBg } ];

    for (let colIndex = this.indexes.commitmentColumn; colIndex <= this.indexes.tvpiColumn; colIndex++) {
      if (this.indexes.blankSpacesColumns.includes(colIndex)) {
        grandTotalRow.push(blankSpace);
        continue;
      }
      if (this.indexes.carriedInterestColumns.includes(colIndex) || colIndex === this.indexes.tvpiColumn) {
        grandTotalRow.push(withCellStyles('', boldGrayBg));
        continue;
      }
      const colLetter = intToExcelCol(colIndex);
      // SUM formula for each column based on the subtotal rows
      grandTotalRow.push({
        f: `SUM(${colLetter}${this.indexes.subTotalRows[0]},${colLetter}${this.indexes.subTotalRows[1]})`,
        t: 'n',
        z: formats.thousandsSeparator,
        s: boldGrayBg,
      });
    }

    return grandTotalRow;
  }

  /**
   * This method calculates and combines the totals for partners' capital, profits, and losses
   * from the financial statement items, and then arranges these totals in a single row.
   */
  private addAssemblyTotalRow(capitalAccountData: CapitalAccountData): Row {
    const row: Row = [];

    const partnersCapitalTotals = capitalAccountData.partnersCapital.financialStatementItems.map(
      (item) => getCell({ v: item.sums.balance, z: formats.thousandsSeparator, t: 'n', s: bold }),
    );
    const profitsTotals = capitalAccountData.profits.financialStatementItems.map(
      (item) => getCell({ v: item.sums.balance, z: formats.thousandsSeparator, t: 'n', s: bold }),
    );
    const lossesTotals = capitalAccountData.losses.financialStatementItems.map(
      (item) => getCell({ v: item.sums.balance, z: formats.thousandsSeparator, t: 'n', s: bold }),
    );

    const combinedTotals = capitalAccountData.partnersCapital.sums.balance
      + capitalAccountData.profits.sums.balance
      + capitalAccountData.losses.sums.balance;

    const combinedTotalsCell = getCell({ v: combinedTotals, z: formats.thousandsSeparator, t: 'n' });

    // fill the columns corresponding to investor details like id, name, commitment, holdings
    row.push(withCellStyles('Assembly totals', bold), ...Array(this.indexes.firstItemColumn - 2).fill(blankSpace));

    const accResultsAndCarriedInterest = Array(6).fill(blankSpace);

    row.push(...partnersCapitalTotals, blankSpace, ...profitsTotals, blankSpace, ...lossesTotals, blankSpace,
      ...Array(capitalAccountData.customAllocations.length), ...accResultsAndCarriedInterest, combinedTotalsCell);

    return row;
  }

  private addCheckRow(): Row {
    const checkRow: Row = [withCellStyles('Check', bold), ...Array(5)];

    const grandTotalRowIndex = this.indexes.subTotalRows[1] + 1; // as it is under the last subtotal row
    const assemblyTotalRowIndex = grandTotalRowIndex + 1; // as it is under the grand total row

    for (let colIndex = this.indexes.firstItemColumn; colIndex <= this.indexes.navColumn; colIndex++) {
      if ((colIndex >= this.indexes.customAllocationsColumns.at(0) && colIndex < this.indexes.navColumn)
        || this.indexes.blankSpacesColumns.includes(colIndex)) {
        checkRow.push(blankSpace);
        continue;
      }
      const colLetter = intToExcelCol(colIndex);
      // Calculate the difference
      checkRow.push({
        f: `${colLetter}${assemblyTotalRowIndex} - ${colLetter}${grandTotalRowIndex}`,
        z: formats.thousandsSeparator,
        t: 'n' ,
        s: boldRed,
      });
    }

    return checkRow;
  }

  private removeLeadingZerosFromId(id: string): string {
    const parts = id.split('-');
    const partsWithoutLeadingZeros = parts.map((part) => part.replace(/^0+/, ''));
    return partsWithoutLeadingZeros.join('-');
  }

  private parseGP(gpValue: boolean | string | number): boolean {
    return (
      gpValue === true ||
      gpValue === 1 ||
      String(gpValue).toLowerCase() === 'gp'
    );
  }

  private parseCommitment(commitment: string | number): number {
    return parseFloat(String(commitment).replace(/,/g, '')) || 0;
  }

  /**
   * This function is useful when headers from external sources might have
   * unexpected spaces or characters (could be the case whitespaces are added while uploading),
   * and we need a consistent way to reference them.
   */
  private mapTrimmedHeaders(headers: string[]): Record<string, string> {
    const headerMapping = {};
    for (const header of headers) {
      const trimmedHeader = header.trim();
      headerMapping[trimmedHeader] = header;
    }
    return headerMapping;
  }

  /**
   * Sets greater row heights for headers, and Limited Partners(LP)/General Partners(GP) rows
   */
  private setManualRowHeights(sheet: XLSX.WorkSheet) {
    setRowHeight(sheet, 0, sizes.headersRowHeight);
    setRowHeight(sheet, this.indexes.lpsStart - 2, sizes.headersRowHeight);
    setRowHeight(sheet, this.indexes.gpsStart - 2, sizes.headersRowHeight);
  }

  private setManualColumnWidths(sheet: WorkSheet) {
    this.indexes.blankSpacesColumns.forEach((index) => setColumnWidth(sheet, index - 1, sizes.blankSpaceColumnWidth));
    this.indexes.holdingsColumns.forEach((index) => setColumnWidth(sheet, index - 1, sizes.pctColumnWidth));
    setColumnWidth(sheet, this.indexes.carriedInterestColumns.at(0) - 1, sizes.pctColumnWidth);
  }

  private findFirstPlItemName(capitalAccountData: CapitalAccountData): string {
    return capitalAccountData.profits.financialStatementItems[0]?.name || capitalAccountData.profits.financialStatementItems[0]?.name;
  }
}

const blankSpace = '';

/**
 * may contain some fuckery due to one-based and zero-based indexing, which could be the reason +-1 or even +-2 are used
 */
class Indexes {
  lpsStart: number;
  gpsStart: number;
  commitmentColumn: number;
  holdingsColumns: number[];
  firstItemColumn: number;
  itemsColumns: Map<string, number> = new Map();
  customAllocationsColumns: number[];
  accumulatedResultsColumn: number;
  carriedInterestColumns: number[];
  navColumn: number;
  nglColumn: number;
  subTotalRows: number[];
  assemblyTotalRow: number;
  blankSpacesColumns: number[];
  tvpiColumn: number;

  constructor() {
    this.lpsStart = 0;
    this.gpsStart = 0;
    this.commitmentColumn = 0;
    this.holdingsColumns = [];
    this.firstItemColumn = 0;
    this.customAllocationsColumns = [];
    this.accumulatedResultsColumn = 0;
    this.carriedInterestColumns = [];
    this.navColumn = 0;
    this.nglColumn = 0;
    this.subTotalRows = [];
    this.blankSpacesColumns = [];
    this.tvpiColumn = 0;
  }
}

const sizes = {
  blankSpaceColumnWidth: 1.5,
  pctColumnWidth: 8,
  headersRowHeight: 30,
};
